import type { ILog, IQueryURL } from '@monofe/util';
import type { IHistory } from '../history/interface';
import type { IRoute, IRouter } from './interface';

function Router(query: IQueryURL, history: IHistory, log: ILog): IRouter {
    const routes: Record<string, IRoute<unknown>> = {};

    const actionPopCount = (popCount: number): boolean => {
        const canPop = popCount <= (history.length() ?? -1);

        if (canPop) {
            history.go(-popCount);
        }

        return canPop;
    };

    const actionPush = (name: string, args: unknown, options?: { replace?: boolean }) => {
        const route = routes[name];

        if (!route) {
            return;
        }

        route
            .push(history, args)
            .then(result => {
                if (result.didLaunchAction && route) {
                    /* Done */
                } else if (result.alternativeRouteSuggested) {
                    actionPush(
                        result.alternativeRouteSuggested,
                        result.alternativeRoutePayload ?? {},
                        options,
                    );
                }
            })
            .catch((error: Error) => {
                log.error(`Route push failed: ${error.message}`);
            });
    };

    return {
        registerRoute: (name: string, route: IRoute<unknown>) => (routes[name] = route),
        unregisterRoute: (name: string) => delete routes[name],
        push: (name: string, args: unknown, options?: { replace?: boolean }) => {
            actionPush(name, args, options);
        },

        popTo: function (name: string): boolean {
            const routeTarget = routes[name];

            if (!routeTarget) {
                return false;
            }

            const curIdx = history.findFirstPathPrefix(routeTarget.paths) ?? -1;

            if (curIdx >= 0) {
                actionPopCount(-curIdx);
            }

            const didPop = curIdx >= 0;
            return didPop;
        },

        pop: () => actionPopCount(1),

        popCount: (popCount: number) => actionPopCount(popCount),

        allRoutes: () => Object.values(routes),
    };
}

export default Router;
